import { useTranslation } from '@silospot/i18n/react';
import type { FraudData, ThreatRow } from '@silospot/shared';
import { useQuery } from '@tanstack/react-query';
import { type ReactElement, type ReactNode, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Badge,
  Card,
  CardHead,
  Chip,
  EstimatedNote,
  Hint,
  Kpi,
  KpiGrid,
  PageHeader,
  RiskPill,
  Segmented,
  Verdict,
} from '../../components/cld';
import { Donut } from '../../components/cld/charts';
import { Flag } from '../../components/location';
import { api } from '../../lib/api';
import { usePeriodRange } from '../../stores/period-store';

const SIGNAL_COLOR: Record<string, string> = {
  bot: '#7c3aed',
  headless: '#64748b',
  proxy: '#0891b2',
  datacenter: '#d97706',
  fingerprint: '#dc2626',
  velocity: '#ea580c',
  tor: '#334155',
  risk: '#db2777',
};
function signalColor(s: string): string {
  return SIGNAL_COLOR[s] ?? '#98a0ae';
}

export function FraudPage(): ReactElement {
  const { t } = useTranslation('pages');
  const { t: ts } = useTranslation('shell');
  const { id = '' } = useParams();
  const range = usePeriodRange();
  const [view, setView] = useState<'synthese' | 'investigation' | 'matrice'>('synthese');
  const [filter, setFilter] = useState<string>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['cld-fraud', id, range],
    queryFn: () => api.getFraud(id, range),
    enabled: !!id,
  });

  const nf = new Intl.NumberFormat('fr-FR');
  const fmt = (n: number) => nf.format(n);

  const header = (
    <PageHeader
      title={ts('nav.fraud')}
      sub={t('fraud.subtitle')}
      actions={
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { value: 'synthese', label: t('fraud.viewSynthese') },
            { value: 'investigation', label: t('fraud.viewInvestigation') },
            { value: 'matrice', label: t('fraud.viewMatrice') },
          ]}
        />
      }
    />
  );

  if (isLoading || !data) {
    return (
      <div>
        {header}
        <Card>
          <div className="h-64 animate-pulse rounded-lg bg-neutral-bg" />
        </Card>
      </div>
    );
  }

  const k = data.kpis;
  const filtered = filter === 'all' ? data.feed : data.feed.filter((e) => e.signal === filter);
  const filterChips = ['all', ...data.composition.map((c) => c.signal)];

  return (
    <div>
      {header}

      <KpiGrid cols={4}>
        <Kpi label={t('fraud.kpiFraudSessions')} value={fmt(k.fraudSessions)} />
        <Kpi label={t('signal.bot')} value={fmt(k.bots)} />
        <Kpi
          label={t('fraud.kpiFraudClicks')}
          value={<Locked hint={t('fraud.kpiFraudClicks')} />}
        />
        <Kpi label={t('fraud.kpiProxy')} value={fmt(k.proxyVpnTor)} />
      </KpiGrid>

      <div className="mt-4">
        {view === 'synthese' && <Synthese data={data} fmt={fmt} />}
        {view === 'investigation' && (
          <Card className="p-0">
            <div className="flex flex-wrap items-center gap-2 border-b border-border p-3.5">
              {filterChips.map((f) => (
                <Chip key={f} active={filter === f} onClick={() => setFilter(f)}>
                  {f === 'all' ? t('fraud.allSignals') : t(`signal.${f}`)}
                </Chip>
              ))}
              <div className="flex-1" />
              <span className="text-[12.5px] text-text-3">
                {filtered.length} · {t('fraud.realtime')}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr>
                    {[
                      t('fraud.colTime'),
                      t('fraud.colThreatType'),
                      t('fraud.colIp'),
                      t('fraud.colCountry'),
                      t('fraud.colPage'),
                      t('fraud.colVerdict'),
                      t('fraud.colAction'),
                    ].map((h, i) => (
                      <th
                        key={h}
                        className={`whitespace-nowrap border-b border-border px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-text-3 ${i >= 5 ? 'text-right' : 'text-left'}`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((e) => (
                    <tr key={e.visitId} className="hover:bg-[#fafbfc]">
                      <td className="whitespace-nowrap border-b border-[#f1f3f6] px-3 py-2.5 font-mono text-[12px] text-text-3">
                        {e.arrivedAt.slice(5, 16)}
                      </td>
                      <td className="whitespace-nowrap border-b border-[#f1f3f6] px-3 py-2.5">
                        <span className="inline-flex items-center gap-2">
                          <span
                            className="h-2 w-2 rounded-full"
                            style={{ background: signalColor(e.signal) }}
                          />
                          {t(`signal.${e.signal}`)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap border-b border-[#f1f3f6] px-3 py-2.5 font-mono text-text">
                        {e.ip}
                      </td>
                      <td className="whitespace-nowrap border-b border-[#f1f3f6] px-3 py-2.5">
                        <Flag country={e.country} /> {e.country || '—'}
                      </td>
                      <td className="whitespace-nowrap border-b border-[#f1f3f6] px-3 py-2.5 font-mono text-[12px] text-text-2">
                        {e.page || '/'}
                      </td>
                      <td className="whitespace-nowrap border-b border-[#f1f3f6] px-3 py-2.5 text-right">
                        <Verdict score={e.score} size="sm" />
                      </td>
                      <td className="whitespace-nowrap border-b border-[#f1f3f6] px-3 py-2.5 text-right">
                        <Badge tone="suspect">{t('action.detected')}</Badge>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-10 text-center text-[13px] text-text-3">
                        —
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </Card>
        )}
        {view === 'matrice' && (
          <Card>
            <CardHead title={t('fraud.matrixTitle')} sub={t('fraud.matrixSub')} />
            <Matrix data={data} />
          </Card>
        )}
      </div>

      <div className="mt-4">
        <EstimatedNote />
      </div>
    </div>
  );
}

function Synthese({ data, fmt }: { data: FraudData; fmt: (n: number) => string }): ReactElement {
  const { t } = useTranslation('pages');
  const top = [...data.composition].sort((a, b) => b.count - a.count);
  const donut = top.slice(0, 5).map((c) => ({
    label: t(`signal.${c.signal}`),
    value: c.count,
    color: signalColor(c.signal),
  }));
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3.5 md:grid-cols-3 lg:grid-cols-4">
        {top.map((c) => (
          <Card key={c.signal} className="p-4">
            <span
              className="grid h-7 w-7 place-items-center rounded-[7px]"
              style={{ background: `${signalColor(c.signal)}1f`, color: signalColor(c.signal) }}
            >
              <span className="h-2 w-2 rounded-full bg-current" />
            </span>
            <div className="mt-3 font-mono text-[22px] font-bold text-text">{fmt(c.count)}</div>
            <div className="mt-0.5 flex items-center gap-1.5 text-[12.5px] text-text-2">
              {t(`signal.${c.signal}`)}
              <Hint text={t(`signal.${c.signal}`)} />
            </div>
          </Card>
        ))}
        <Card className="flex flex-col justify-center border-dashed bg-[#fbfcfe] p-4">
          <div className="flex items-center gap-1.5 text-[12.5px] text-text-2">
            {t('fraud.autoBlock')}
            <Hint text={t('fraud.autoBlock')} />
          </div>
          {/* No Cloudflare enforcement yet → not a real figure. */}
          <div className="mt-2 font-mono text-[22px] font-bold text-text-3">—</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1.4fr]">
        <Card>
          <CardHead title={t('fraud.compTitle')} sub={t('fraud.compSub')} />
          <div className="flex items-center gap-4">
            <Donut data={donut} size={176} />
            <div className="flex flex-1 flex-col gap-2.5">
              {donut.map((d) => (
                <div key={d.label} className="flex items-center gap-2 text-[12.5px]">
                  <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: d.color }} />
                  <span className="flex-1 text-[#3a4151]">{d.label}</span>
                  <span className="font-mono text-text-2">{fmt(d.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
        <Card>
          <CardHead
            title={t('fraud.liveDetect')}
            right={
              <Badge tone="fraud" dot>
                live
              </Badge>
            }
          />
          <Feed rows={data.feed} n={7} />
        </Card>
      </div>
    </div>
  );
}

function Feed({ rows, n }: { rows: ThreatRow[]; n: number }): ReactNode {
  const { t } = useTranslation('pages');
  const items = rows.slice(0, n);
  if (items.length === 0) return <p className="py-8 text-center text-[13px] text-text-3">—</p>;
  return (
    <div className="flex flex-col">
      {items.map((e, i) => (
        <div
          key={e.visitId}
          className={`grid grid-cols-[8px_1fr_auto] items-center gap-3 py-2.5 ${i < items.length - 1 ? 'border-b border-[#f1f3f6]' : ''}`}
        >
          <span className="h-2 w-2 rounded-full" style={{ background: signalColor(e.signal) }} />
          <div className="min-w-0">
            <div className="truncate text-[13px] font-medium text-text">
              {t(`signal.${e.signal}`)}{' '}
              <span className="font-normal text-text-3">· {e.page || '/'}</span>
            </div>
            <div className="truncate font-mono text-[11.5px] text-text-3">
              {e.ip} · <Flag country={e.country} /> {e.country} · {e.arrivedAt.slice(5, 16)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <RiskPill value={e.score} small />
            <Badge tone="suspect">{t('action.detected')}</Badge>
          </div>
        </div>
      ))}
    </div>
  );
}

function Matrix({ data }: { data: FraudData }): ReactElement {
  const { t } = useTranslation('pages');
  const sources = [...new Set(data.matrix.map((c) => c.sourceType))];
  const signals = [...new Set(data.matrix.map((c) => c.signal))];
  const lookup = new Map(data.matrix.map((c) => [`${c.sourceType}|${c.signal}`, c.count]));
  const max = Math.max(1, ...data.matrix.map((c) => c.count));
  return (
    <div className="overflow-x-auto">
      <table className="border-collapse text-[12.5px]">
        <thead>
          <tr>
            <th className="px-2 py-2 text-left text-[11px] font-semibold uppercase text-text-3" />
            {signals.map((s) => (
              <th key={s} className="px-2 py-2 text-center text-[11px] font-semibold text-text-2">
                {t(`signal.${s}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sources.map((src) => (
            <tr key={src}>
              <td className="whitespace-nowrap py-1 pr-3 font-medium text-[#3a4151]">{src}</td>
              {signals.map((s) => {
                const v = lookup.get(`${src}|${s}`) ?? 0;
                const a = v === 0 ? 0 : 0.1 + (v / max) * 0.8;
                return (
                  <td key={s} className="p-1">
                    <div
                      className="grid h-9 min-w-[52px] place-items-center rounded-md font-mono text-[12px]"
                      style={{
                        background: `rgba(220,38,38,${a.toFixed(2)})`,
                        color: a > 0.5 ? '#fff' : '#5b6472',
                      }}
                    >
                      {v || ''}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Integration-gated value (Google Ads, Palier C): never a fake number. */
function Locked({ hint }: { hint: string }): ReactElement {
  return (
    <span className="inline-flex items-center gap-1 font-mono text-text-3">
      —<Hint text={hint} />
    </span>
  );
}
